<!--
* @Author: AI Assistant
* @Date: 2026-01-12
* @Description: API Discovery 页面 - 核心能力展示与支持模型列表
-->
<template>
    <div
        class="discovery-page p-4 md:p-6 max-w-[1120px] mx-auto space-y-8 md:space-y-12"
    >
        <h1 class="sr-only">{{ t('api.discoveryH1') }}</h1>
        <!-- 核心能力区域 -->
        <section class="capabilities-section">
            <div class="mb-6">
                <h2 class="text-xl md:text-2xl font-bold text-white mb-2">
                    {{ t('api.coreCapabilities') }}
                </h2>
                <p class="text-sm text-zinc-400">
                    {{ t('api.coreCapabilitiesDesc') }}
                </p>
            </div>

            <div
                class="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4"
            >
                <div
                    v-for="capability in capabilities"
                    :key="capability.id"
                    class="capability-card rounded-xl p-4 hover:border-lime-400/50 hover:bg-zinc-900 transition-all cursor-pointer group"
                    @click="handleCapabilityClick(capability)"
                >
                    <div
                        class="w-10 h-10 rounded-lg bg-zinc-900 flex items-center justify-center mb-3 group-hover:bg-lime-400 group-hover:text-black transition-colors"
                    >
                        <img
                            v-if="capability.iconImage"
                            :src="capability.iconImage"
                            :alt="capability.name"
                            class="w-5 h-5 object-contain group-hover:brightness-0"
                        />
                        <component
                            v-else
                            :is="capability.iconComponent"
                            class="w-5 h-5 text-lime-400 group-hover:text-black"
                        />
                    </div>
                    <h3 class="text-white font-medium mb-1">
                        {{ capability.name }}
                    </h3>
                    <p class="text-xs text-zinc-500 line-clamp-2 h-8">
                        {{ capability.description }}
                    </p>
                </div>
            </div>
        </section>

        <!-- 支持模型区域 -->
        <section class="models-section">
            <div
                class="mb-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4"
            >
                <div>
                    <h2 class="text-xl md:text-2xl font-bold text-white mb-2">
                        {{ t('api.supportedModels') }}
                    </h2>
                    <p class="text-sm text-zinc-400">
                        {{ t('api.supportedModelsDesc') }}
                    </p>
                </div>
                <div class="flex gap-2">
                    <button
                        class="px-3 py-1 rounded-full text-[10px] md:text-xs font-bold border transition-colors"
                        :class="
                            activeFilter === 'Video'
                                ? 'bg-lime-400/10 text-lime-400 border-lime-400/20'
                                : 'bg-zinc-800 text-zinc-400 border-zinc-700 hover:border-zinc-600'
                        "
                        @click="activeFilter = 'Video'"
                    >
                        {{ t('api.video') }}
                    </button>
                    <button
                        class="px-3 py-1 rounded-full text-[10px] md:text-xs font-bold border transition-colors"
                        :class="
                            activeFilter === 'Image'
                                ? 'bg-lime-400/10 text-lime-400 border-lime-400/20'
                                : 'bg-zinc-800 text-zinc-400 border-zinc-700 hover:border-zinc-600'
                        "
                        @click="activeFilter = 'Image'"
                    >
                        {{ t('api.image') }}
                    </button>
                    <button
                        class="px-3 py-1 rounded-full text-[10px] md:text-xs font-bold border transition-colors"
                        :class="
                            activeFilter === 'Text'
                                ? 'bg-lime-400/10 text-lime-400 border-lime-400/20'
                                : 'bg-zinc-800 text-zinc-400 border-zinc-700 hover:border-zinc-600'
                        "
                        @click="activeFilter = 'Text'"
                    >
                        Text
                    </button>
                </div>
            </div>

            <!-- 骨架屏 -->
            <div
                v-if="activeFilter === 'Text' ? isTextModelsLoading : isLoading"
                class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4"
            >
                <div
                    v-for="i in 8"
                    :key="i"
                    class="model-card-skeleton flex items-center gap-3 p-3 bg-[#18181b] border border-zinc-800 rounded-lg"
                >
                    <div
                        class="w-10 h-10 rounded bg-zinc-900 flex-shrink-0 border border-zinc-800 skeleton"
                    ></div>
                    <div class="min-w-0 flex-1">
                        <div
                            class="h-4 bg-zinc-900 rounded skeleton mb-2"
                        ></div>
                        <div class="flex gap-1">
                            <div
                                class="h-5 w-12 bg-zinc-900 rounded skeleton"
                            ></div>
                            <div
                                class="h-5 w-16 bg-zinc-900 rounded skeleton"
                            ></div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- 模型列表 -->
            <div
                v-else
                class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4"
            >
                <div
                    v-for="model in filteredModels"
                    :key="model.id"
                    class="model-card flex items-center gap-3 p-3 bg-[#18181b] border border-zinc-800 rounded-lg hover:border-zinc-600 transition-colors"
                >
                    <div
                        class="w-10 h-10 rounded bg-zinc-900 flex-shrink-0 flex items-center justify-center border border-zinc-800 overflow-hidden"
                        :class="
                            model.type === 'Video'
                                ? 'text-lime-400'
                                : model.type === 'Text'
                                    ? 'text-purple-400'
                                    : 'text-blue-400'
                        "
                    >
                        <img
                            v-if="model.thumbnail"
                            :src="model.thumbnail"
                            :alt="model.name"
                            class="w-full h-full object-cover"
                        />
                        <span v-else class="text-[10px] font-bold uppercase">
                            {{ model.type === 'Video' ? 'VID' : model.type === 'Text' ? 'TXT' : 'IMG' }}
                        </span>
                    </div>
                    <div class="min-w-0">
                        <h4 class="text-sm font-medium text-white truncate">
                            {{ model.name }}
                        </h4>
                        <div class="flex gap-1 mt-1 flex-wrap">
                            <span
                                v-for="tag in model.tags.slice(0, 2)"
                                :key="tag"
                                class="text-[10px] px-1.5 py-0.5 rounded bg-zinc-900 text-zinc-500 border border-zinc-800 whitespace-nowrap"
                            >
                                {{ tag }}
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, h, onMounted } from 'vue'
import { navigateTo, useHead } from '#imports'
import { useI18n } from '~/composables/useI18n'
import { useAppStore } from '~/stores/app'
import { useMultilingualSeo } from '~/composables/useMultilingualSeo'
import { getChatModelsApi, type ChatModelItem } from '~/api/chat'

const { t } = useI18n()
const appStore = useAppStore()
// 使用多语言 SEO
const { canonicalUrl } = useMultilingualSeo({
    pageType: 'apiDiscovery',
    robots: 'noindex, follow',
})

// 导入图标图片
import textToImageIcon from '~/assets/images/api/text-to-image.png'
import imageToImageIcon from '~/assets/images/api/image-to-image.png'
import textToVideoIcon from '~/assets/images/api/text-to-video.png'
import imageToVideoIcon from '~/assets/images/api/image-to-video.png'
import multiImageToVideoIcon from '~/assets/images/api/multi-image-to-video.png'
import aiMusicIcon from '~/assets/images/api/ai-music.png'
import digitalAvatarIcon from '~/assets/images/api/digital-avatar.png'

// ========== 图标组件 ==========
const MicIcon = {
    render: () =>
        h(
            'svg',
            {
                xmlns: 'http://www.w3.org/2000/svg',
                width: '20',
                height: '20',
                viewBox: '0 0 24 24',
                fill: 'none',
                stroke: 'currentColor',
                'stroke-width': '2',
                'stroke-linecap': 'round',
                'stroke-linejoin': 'round',
            },
            [
                h('path', { d: 'M12 19v3' }),
                h('path', { d: 'M19 10v2a7 7 0 0 1-14 0v-2' }),
                h('rect', {
                    x: '9',
                    y: '2',
                    width: '6',
                    height: '13',
                    rx: '3',
                }),
            ]
        ),
}

const ChatIcon = {
    render: () =>
        h(
            'svg',
            {
                xmlns: 'http://www.w3.org/2000/svg',
                width: '20',
                height: '20',
                viewBox: '0 0 16 16',
                fill: 'none',
            },
            [
                h('path', {
                    d: 'M12.2121 1.59961C12.7928 1.59961 13.3497 1.84085 13.7603 2.27027C14.1709 2.69968 14.4016 3.28209 14.4016 3.88937V10.0541C14.4016 10.6614 14.1709 11.2438 13.7603 11.6732C13.3497 12.1026 12.7928 12.3439 12.2121 12.3439H9.04774L5.72591 14.3292C5.40058 14.5236 4.9989 14.2931 4.97139 13.9118L4.96998 13.8704V12.3439H3.79104C3.22241 12.3439 2.67609 12.1125 2.26767 11.6988C1.85924 11.285 1.62076 10.7213 1.60269 10.1269L1.60156 10.0541V3.88937C1.60156 3.28209 1.83224 2.69968 2.24284 2.27027C2.65345 1.84085 3.21035 1.59961 3.79104 1.59961H12.2121ZM12.2121 2.65642H3.79104C3.47836 2.65642 3.17849 2.78632 2.95739 3.01755C2.7363 3.24877 2.61209 3.56237 2.61209 3.88937V10.0541C2.61209 10.3811 2.7363 10.6947 2.95739 10.9259C3.17849 11.1572 3.47836 11.2871 3.79104 11.2871H5.47525C5.60925 11.2871 5.73777 11.3427 5.83252 11.4418C5.92728 11.5409 5.98051 11.6753 5.98051 11.8155V12.9598L8.66318 11.3569C8.72407 11.3205 8.79157 11.2977 8.86135 11.29L8.91384 11.2871H12.2121C12.5248 11.2871 12.8246 11.1572 13.0457 10.9259C13.2668 10.6947 13.391 10.3811 13.391 10.0541V3.88937C13.391 3.56237 13.2668 3.24877 13.0457 3.01755C12.8246 2.78632 12.5248 2.65642 12.2121 2.65642Z',
                    fill: 'currentColor',
                }),
                h('path', {
                    d: 'M4.51019 5.02038H7.06115C7.19646 5.02038 7.32623 4.96663 7.42191 4.87095C7.51758 4.77527 7.57134 4.6455 7.57134 4.51019C7.57134 4.37488 7.51758 4.24511 7.42191 4.14943C7.32623 4.05375 7.19646 4 7.06115 4H4.51019C4.37488 4 4.24511 4.05375 4.14943 4.14943C4.05375 4.24511 4 4.37488 4 4.51019C4 4.6455 4.05375 4.77527 4.14943 4.87095C4.24511 4.96663 4.37488 5.02038 4.51019 5.02038Z',
                    fill: 'currentColor',
                }),
            ]
        ),
}

// ========== 类型定义 ==========
interface Capability {
    id: string
    name: string
    description: string
    endpoint: string
    route?: string
    iconComponent?: any
    iconImage?: string
}

interface Model {
    id: string
    name: string
    type: 'Video' | 'Image' | 'Text'
    tags: string[]
    thumbnail?: string
}

interface ApiModel {
    model_key: string
    title: string
    desc: string
    thumbnail?: string
    marks: string[]
    category?: {
        code: string
        title: string
        description: string
    }
    is_new?: boolean
    is_hot?: boolean
}

interface ApiResponse {
    image_models?: {
        text_to_image?: ApiModel[]
        image_to_image?: ApiModel[]
    }
    video_models?: {
        text_to_video?: ApiModel[]
        image_to_video?: ApiModel[]
    }
}

// ========== 能力数据（使用 computed 实现响应式国际化） ==========
const capabilities = computed<Capability[]>(() => {
    const items: Capability[] = [
        {
            id: 'text-to-image',
            name: t('api.textToImage'),
            description: t('api.textToImageDesc'),
            endpoint: '/v1/generation/text-to-image',
            route: '/text-to-image',
            iconImage: textToImageIcon,
        },
        {
            id: 'image-to-image',
            name: t('api.imageToImage'),
            description: t('api.imageToImageDesc'),
            endpoint: '/v1/generation/image-to-image',
            route: '/image-to-image',
            iconImage: imageToImageIcon,
        },
        {
            id: 'text-to-video',
            name: t('api.textToVideo'),
            description: t('api.textToVideoDesc'),
            endpoint: '/v1/generation/text-to-video',
            route: '/text-to-video',
            iconImage: textToVideoIcon,
        },
        {
            id: 'image-to-video',
            name: t('api.imageToVideo'),
            description: t('api.imageToVideoDesc'),
            endpoint: '/v1/generation/image-to-video',
            route: '/image-to-video',
            iconImage: imageToVideoIcon,
        },
        {
            id: 'multi-image-to-video',
            name: t('api.multiImageToVideo'),
            description: t('api.multiImageToVideoDesc'),
            endpoint: '/v1/generation/multi-image-to-video',
            route: '/image-to-video',
            iconImage: multiImageToVideoIcon,
        },
        {
            id: 'podcast',
            name: t('api.podcast'),
            description: t('api.podcastDesc'),
            endpoint: '/v1/tools/podcast',
            iconComponent: MicIcon,
        },
        {
            id: 'music',
            name: t('api.music'),
            description: t('api.musicDesc'),
            endpoint: '/v1/tools/music',
            route: '/ai-music',
            iconImage: aiMusicIcon,
        },
        {
            id: 'digital_avatar',
            name: t('api.digitalAvatar'),
            description: t('api.digitalAvatarDesc'),
            endpoint: '/v1/tools/digital_avatar_video',
            route: '/characters',
            iconImage: digitalAvatarIcon,
        },
        {
            id: 'chat',
            name: t('api.chat'),
            description: t('api.chatDesc'),
            endpoint: '/v1/chat/completions',
            route: '/chat',
            iconComponent: ChatIcon,
        },
    ]

    if (!appStore.hideCharacterEntries) {
        return items
    }

    return items.filter(item => item.route !== '/characters')
})

// ========== 点击处理 ==========
const handleCapabilityClick = (capability: Capability) => {
    if (capability.route) {
        // 如果是多图生视频，添加 query 参数来指定 tab
        if (capability.id === 'multi-image-to-video') {
            navigateTo({
                path: capability.route,
                query: { tab: 'multi' }
            })
        } else {
            navigateTo(capability.route)
        }
    }
}

const models = ref<Model[]>([])
const isLoading = ref(true)

// ========== 数据转换 ==========
const transformApiModelToModel = (
    apiModel: ApiModel,
    type: 'Video' | 'Image'
): Model => {
    const tags: string[] = []

    // 添加 marks
    if (apiModel.marks && apiModel.marks.length > 0) {
        tags.push(...apiModel.marks)
    }

    // 添加 category title
    if (apiModel.category?.title) {
        tags.push(apiModel.category.title)
    }

    // 添加 is_new 和 is_hot 标记
    if (apiModel.is_new) {
        tags.push('New')
    }
    if (apiModel.is_hot) {
        tags.push('Hot')
    }

    return {
        id: apiModel.model_key,
        name: apiModel.title,
        type,
        tags: tags.length > 0 ? tags : ['Standard'],
        thumbnail: apiModel.thumbnail,
    }
}

// ========== 获取模型列表 ==========
const fetchModels = async () => {
    try {
        isLoading.value = true
        
        // 从 S3 静态资源服务器读取 JSON 文件
        // 带时间戳用于开发调试，生产环境会通过 HTTP 响应头实现强制缓存
        const response = await fetch('https://static.weryai.com/cms/openapi-model-info.json')
        
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`)
        }
        
        const data = (await response.json()) as ApiResponse
        
        console.log('📦 从 S3 加载模型数据:', {
            url: 'https://static.weryai.com/cms/openapi-model-info.json',
            cacheStatus: response.headers.get('cache-control'),
            etag: response.headers.get('etag'),
            timestamp: new Date().toLocaleTimeString()
        })

        if (data) {
            const { image_models, video_models } = data
            const modelList: Model[] = []

            // 处理图片模型
            if (image_models) {
                if (image_models.text_to_image) {
                    image_models.text_to_image.forEach((model: ApiModel) => {
                        modelList.push(transformApiModelToModel(model, 'Image'))
                    })
                }
                if (image_models.image_to_image) {
                    image_models.image_to_image.forEach((model: ApiModel) => {
                        // 避免重复添加相同的模型
                        if (!modelList.find(m => m.id === model.model_key)) {
                            modelList.push(
                                transformApiModelToModel(model, 'Image')
                            )
                        }
                    })
                }
            }

            // 处理视频模型
            if (video_models) {
                if (video_models.text_to_video) {
                    video_models.text_to_video.forEach((model: ApiModel) => {
                        modelList.push(transformApiModelToModel(model, 'Video'))
                    })
                }
                if (video_models.image_to_video) {
                    video_models.image_to_video.forEach((model: ApiModel) => {
                        // 避免重复添加相同的模型
                        if (!modelList.find(m => m.id === model.model_key)) {
                            modelList.push(
                                transformApiModelToModel(model, 'Video')
                            )
                        }
                    })
                }
            }

            models.value = modelList
            console.log('✅ 模型数据加载成功:', {
                imageModels: modelList.filter(m => m.type === 'Image').length,
                videoModels: modelList.filter(m => m.type === 'Video').length,
                total: modelList.length
            })
        }
    } catch (error) {
        console.error('❌ 加载模型数据失败:', error)
    } finally {
        isLoading.value = false
    }
}

const textModels = ref<Model[]>([])
const isTextModelsLoading = ref(true)

const fetchTextModels = async () => {
    try {
        isTextModelsLoading.value = true
        const res = await getChatModelsApi()
        const list: ChatModelItem[] = res?.data?.models || res?.models || []
        textModels.value = list.map((m) => ({
            id: m.model,
            name: m.desc || m.model,
            type: 'Text' as const,
            tags: ['LLM'],
            thumbnail: m.icon,
        }))
    } catch (error) {
        console.error('❌ 加载 Text 模型数据失败:', error)
    } finally {
        isTextModelsLoading.value = false
    }
}

onMounted(() => {
    fetchModels()
    fetchTextModels()
})

// ========== 筛选逻辑 ==========
const activeFilter = ref<'Video' | 'Image' | 'Text'>('Video')

const filteredModels = computed(() => {
    if (activeFilter.value === 'Text') {
        return textModels.value
    }
    return models.value.filter(model => model.type === activeFilter.value)
})


// 结构化数据
useHead({
    script: [
        {
            key: 'breadcrumb-schema',
            type: 'application/ld+json',
            innerHTML: computed(() =>
                JSON.stringify({
                    '@context': 'https://schema.org',
                    '@type': 'BreadcrumbList',
                    itemListElement: [
                        {
                            '@type': 'ListItem',
                            position: 1,
                            name: 'Home',
                            item: 'https://www.weryai.com/',
                        },
                        {
                            '@type': 'ListItem',
                            position: 2,
                            name: t('api.discovery'),
                            item: canonicalUrl.value,
                        },
                    ],
                })
            ),
        },
        {
            key: 'itemlist-schema',
            type: 'application/ld+json',
            innerHTML: computed(() =>
                JSON.stringify({
                    '@context': 'https://schema.org',
                    '@type': 'ItemList',
                    name: t('api.supportedModels'),
                    description: t('api.supportedModelsDesc'),
                    itemListElement: models?.value?.map((model, index) => ({
                        '@type': 'ListItem',
                        position: index + 1,
                        name: model.name,
                        url: canonicalUrl.value,
                    })),
                })
            ),
        },
    ],
})
</script>

<style scoped lang="less">
.discovery-page {
    animation: fadeIn 0.3s ease-out;
}

/* Screen reader only */
.sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
}

@keyframes fadeIn {
    from {
        opacity: 0;
        transform: translateY(10px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

.capability-card {
    background-color: rgba(28, 28, 32, 1);
    border: 1px solid rgba(255, 255, 255, 0.06);

    &:hover {
        transform: translateY(-2px);
        border-color: rgba(163, 230, 53, 0.5); // lime-400/50
    }
}

.model-card {
    &:hover {
        transform: translateX(2px);
    }
}

// 骨架屏样式
.skeleton {
    background: linear-gradient(
        90deg,
        rgba(255, 255, 255, 0.05) 25%,
        rgba(255, 255, 255, 0.1) 50%,
        rgba(255, 255, 255, 0.05) 75%
    );
    background-size: 200% 100%;
    animation: skeleton-loading 1.5s ease-in-out infinite;
}

@keyframes skeleton-loading {
    0% {
        background-position: 200% 0;
    }
    100% {
        background-position: -200% 0;
    }
}

.model-card-skeleton {
    pointer-events: none;
}
</style>
